import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { fetchTodayDispatches, type DispatchListItem } from '@/features/warehouse/api/dispatchApi'
import { warehouseTerms } from '@/features/warehouse/warehouseTerms'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'

function statusBadgeClass(status: string): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'dispatched' || status === 'in_transit') return 'badge-warning'
  if (status === 'receiving_completed') return 'badge-success'
  if (status === 'cancelled') return 'badge-danger'
  return 'badge-draft'
}

function statusLabel(status: string): string {
  return warehouseTerms.dispatchList.status[status] ?? status
}

function lineSummary(item: DispatchListItem): string {
  return warehouseTerms.dispatchList.lineSummary(item.lines.length)
}

/** GET /warehouse/dispatches/today — list pengiriman hari ini (draft + serah + transit). */
export function WarehouseDispatchListPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<DispatchListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    setLoadError(false)
    fetchTodayDispatches()
      .then((res) => {
        if (!cancelled) setItems(res)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => reload(), [reload, refreshKey])

  useEffect(() => {
    const hasPending = items.some((item) => item.status === 'draft' || item.status === 'dispatched')
    if (!hasPending) return undefined
    const timer = window.setInterval(() => setRefreshKey((k) => k + 1), 8_000)
    return () => window.clearInterval(timer)
  }, [items])

  const draftItems = useMemo(() => items.filter((i) => i.status === 'draft'), [items])
  const sentItems = useMemo(() => items.filter((i) => i.status !== 'draft'), [items])

  function openDispatch(item: DispatchListItem) {
    navigate(`/warehouse/dispatches/${item.id}`, { state: { dispatch: item } })
  }

  return (
    <section className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="page-title">{warehouseTerms.dispatchList.title}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">{warehouseTerms.dispatchList.subtitle}</p>
        </div>
        <button
          type="button"
          className="btn-outline shrink-0 min-w-[44px] px-3"
          disabled={isLoading}
          onClick={() => setRefreshKey((k) => k + 1)}
          aria-label={warehouseTerms.dispatchList.refreshAria}
          title={warehouseTerms.dispatchList.refreshAria}
        >
          <span className={`material-symbols-outlined ${isLoading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
      </header>

      <Link to="/warehouse/dispatches/new" className="btn-primary w-full min-h-[44px] text-center block">
        {warehouseTerms.dispatchList.newDispatch}
      </Link>

      {isLoading && <SkeletonList count={3} label={warehouseTerms.dispatchList.loading} />}

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{warehouseTerms.dispatchList.loadError}</span>
          <button type="button" className="btn-outline min-h-[36px] text-body-sm" onClick={() => setRefreshKey((k) => k + 1)}>
            {warehouseTerms.dispatchList.retry}
          </button>
        </div>
      )}

      {!isLoading && !loadError && items.length === 0 && (
        <EmptyState
          icon="local_shipping"
          title={warehouseTerms.dispatchList.emptyTitle}
          description={warehouseTerms.dispatchList.emptyDescription}
        />
      )}

      {!isLoading && !loadError && draftItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-body-sm font-semibold text-on-surface-variant">{warehouseTerms.dispatchList.draftSection}</p>
          {draftItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="card w-full text-left hover:border-primary transition-colors"
              onClick={() => openDispatch(item)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-body-lg font-semibold text-on-surface">→ {item.target_kitchen_name}</p>
                  <p className="text-body-sm text-on-surface-variant mt-1">
                    {lineSummary(item)} · {item.warehouse_delman_name}
                  </p>
                  <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                </div>
                <span className={statusBadgeClass(item.status)}>{statusLabel(item.status)}</span>
              </div>
              <p className="text-body-sm text-primary font-semibold mt-2">{warehouseTerms.dispatchList.continueEdit}</p>
            </button>
          ))}
        </div>
      )}

      {!isLoading && !loadError && sentItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-body-sm font-semibold text-on-surface-variant">{warehouseTerms.dispatchList.sentSection}</p>
          {sentItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="card w-full text-left hover:border-primary transition-colors"
              onClick={() => openDispatch(item)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-body-lg font-semibold text-on-surface">→ {item.target_kitchen_name}</p>
                  <p className="text-body-sm text-on-surface-variant mt-1">
                    {lineSummary(item)} · {item.warehouse_delman_name}
                  </p>
                  <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                </div>
                <span className={statusBadgeClass(item.status)}>{statusLabel(item.status)}</span>
              </div>
              <p className="text-body-sm text-primary font-semibold mt-2">
                {item.status === 'dispatched'
                  ? warehouseTerms.dispatchList.reDispatch
                  : warehouseTerms.dispatchList.viewOnly}
              </p>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
